import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import Loading from '../common/Loading';
import PhoneVerification from '../common/PhoneVerification';
import {
  COMMUTE_TIME_OPTIONS,
  ESTIMATE_TYPES,
  PAYMENT_METHODS,
  PLACE_OPTIONS,
  STOP_COUNTS,
  TAX_BILL_OPTIONS,
  TIME_OPTIONS,
  TRAVEL_TYPES,
  VEHICLE_NUMBERS,
  VEHICLE_TYPES,
  WAY_TYPES,
  WORK_DAYS,
  WORK_TYPES,
} from '../../const/estimateOptions';
import { MEMO_MAX_LENGTH, blockNonDigitKey, onlyDigits, validateEstimateForm } from '../../utils/validation';
import PlaceAutocomplete from './PlaceAutocomplete';

const today = () =>
  new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const initialForm = () => ({
  name: '',
  phone: '',
  email: '',
  password: '',
  emailReceipt: false,
  companyName: '',
  travelType: '일반여행',
  vehicleType: '',
  vehicleNumber: '',
  boardingCount: '',
  memberCount: '',
  departDate: today(),
  departTime: '05:00',
  arrivalDate: today(),
  arrivalTime: '05:00',
  commuteStartTime: '07:00',
  commuteEndTime: '18:00',
  departPlace: '',
  arrivalPlace: '',
  stopCount: '0',
  stopPlace: '',
  workType: '',
  workDays: '',
  wayType: '왕복',
  payment: '현금',
  taxBill: '발급안함',
  memo: '',
});

/** 라벨이 붙은 선택 상자 (반응형 전폭) */
const SelectField = ({ label, name, value, onChange, options, error, helperText, required }) => (
  <FormControl fullWidth size='small' error={Boolean(error)} required={required}>
    <InputLabel id={`${name}-label`}>{label}</InputLabel>
    <Select
      labelId={`${name}-label`}
      input={<OutlinedInput label={label} />}
      name={name}
      value={value}
      onChange={onChange}
      MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
      sx={{ backgroundColor: '#FCFCFC' }}
    >
      {options.map((option) => {
        const optionValue = typeof option === 'string' ? option : option.value;
        const optionLabel = typeof option === 'string' ? option : option.label;
        return (
          <MenuItem key={`${name}-${optionValue}`} value={optionValue}>
            {optionLabel}
          </MenuItem>
        );
      })}
    </Select>
    {(error || helperText) && <FormHelperText>{error || helperText}</FormHelperText>}
  </FormControl>
);

/** 라디오 묶음 (반응형 전폭) */
const RadioField = ({ label, name, value, onChange, options }) => (
  <Stack
    sx={{
      width: '100%',
      backgroundColor: '#FCFCFC',
      border: '1px solid lightgrey',
      borderRadius: '10px',
      p: 1,
    }}
  >
    <FormLabel sx={{ fontSize: 14 }}>{label}</FormLabel>
    <RadioGroup row name={name} value={value} onChange={onChange} sx={{ justifyContent: 'center' }}>
      {options.map((option) => (
        <FormControlLabel
          key={`${name}-${option}`}
          name={name}
          value={option}
          control={<Radio size='small' />}
          label={option}
        />
      ))}
    </RadioGroup>
  </Stack>
);

/**
 * 일반 버스대절 / 통근·셔틀버스 견적 요청 폼.
 * PC와 모바일이 같은 컴포넌트를 쓰고, 화면 폭에 따라 1열(xs) / 2열(md)로만 바뀐다.
 */
export default function EstimateForm({
  estimateType = ESTIMATE_TYPES.GENERAL,
  accentColor = '#5A4231',
  mode = 'create',
  estimateId,
  auth,
  initialValues,
  onUpdated,
}) {
  const isCommute = estimateType === ESTIMATE_TYPES.COMMUTE;
  const isModify = mode === 'modify';

  const [form, setForm] = useState(() => ({ ...initialForm(), ...(initialValues || {}) }));
  // 수정 모드는 이미 본인확인을 마친 상태로 진입한다
  const [verificationToken, setVerificationToken] = useState(isModify ? auth?.verificationToken : null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const minDate = useMemo(today, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const numericFields = ['phone', 'memberCount', 'boardingCount'];
    let nextValue = type === 'checkbox' ? checked : value;
    if (numericFields.includes(name)) nextValue = onlyDigits(nextValue);

    setForm((prev) => ({ ...prev, [name]: nextValue }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    // 이메일 필수 여부가 '이메일로 견적받기'에 달려 있다. 체크를 끄면 이메일 오류도 함께 푼다.
    if (name === 'emailReceipt' && !nextValue && errors.email) {
      setErrors((prev) => ({ ...prev, email: '' }));
    }
  };

  const closeToast = () => setToast((prev) => ({ ...prev, open: false }));

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextErrors = validateEstimateForm(form, estimateType);
    if (!verificationToken) nextErrors.phone = '휴대폰 본인확인을 먼저 진행해주세요.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setToast({ open: true, severity: 'error', message: '입력하신 내용을 다시 확인해주세요.' });
      const firstKey = Object.keys(nextErrors)[0];
      document.getElementsByName(firstKey)?.[0]?.focus();
      return;
    }

    const payload = {
      estimateType,
      name: form.name.trim(),
      phone: form.phone,
      email: form.email.trim(),
      verificationToken,
      emailReceipt: form.emailReceipt,
      travelType: isCommute ? '기타단체' : form.travelType,
      vehicleType: form.vehicleType,
      vehicleNumber: Number(form.vehicleNumber || 0),
      // 총 인원은 '탑승인원 x 차량대수' 로 계산해 보낸다(입력칸은 없앰)
      memberCount: Number(form.boardingCount || 0) * Number(form.vehicleNumber || 0),
      boardingCount: Number(form.boardingCount || 0),
      departDate: isCommute ? '' : `${form.departDate}T${form.departTime}`,
      arrivalDate: isCommute ? '' : `${form.arrivalDate}T${form.arrivalTime}`,
      departPlace: form.departPlace.trim(),
      arrivalPlace: form.arrivalPlace.trim(),
      stopPlace: form.stopPlace.trim(),
      wayType: isCommute ? '왕복' : form.wayType,
      payment: form.payment,
      taxBill: form.taxBill,
      memo: form.memo.trim(),
      companyName: isCommute ? form.companyName.trim() : '',
      commuteStartTime: isCommute ? form.commuteStartTime : '',
      commuteEndTime: isCommute ? form.commuteEndTime : '',
      stopCount: isCommute ? Number(form.stopCount || 0) : 0,
      workType: isCommute ? form.workType : '',
      workDays: isCommute ? form.workDays : '',
    };

    setLoading(true);
    const request = isModify
      ? axios.put(`/estimate/${estimateId}`, { ...payload, authPhone: auth?.phone })
      : axios.post('/estimate', payload);

    request
      .then(() => {
        setToast({
          open: true,
          severity: 'success',
          message: isModify
            ? '견적 내용을 수정했습니다.'
            : '견적을 요청했습니다. 담당자가 확인 후 연락드립니다.',
        });
        if (isModify) onUpdated?.();
        else {
          setForm(initialForm());
          // 네이버 검색광고 전환(견적요청) — 새로 낸 것만. 추적 실패가 화면을 막으면 안 된다 (public/naver-wcs.js)
          try { window.naverConversion?.(); } catch (e) { /* 무시 */ }
        }
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        setToast({ open: true, severity: 'error', message });
      })
      .finally(() => setLoading(false));
  };

  const field = (props) => ({
    fullWidth: true,
    size: 'small',
    variant: 'outlined',
    onChange: handleChange,
    sx: { backgroundColor: '#FCFCFC' },
    ...props,
  });

  return (
    <Box component='form' onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
      <Grid container spacing={2}>
        {isCommute && (
          <Grid item xs={12}>
            <TextField
              {...field({
                label: '회사명',
                name: 'companyName',
                value: form.companyName,
                error: Boolean(errors.companyName),
                helperText: errors.companyName,
                required: true,
              })}
            />
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <TextField
            {...field({
              label: '이름',
              name: 'name',
              value: form.name,
              error: Boolean(errors.name),
              helperText: errors.name,
              required: true,
            })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <PhoneVerification
            phone={form.phone}
            onPhoneChange={(value) => {
              setForm((prev) => ({ ...prev, phone: value }));
              if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));
            }}
            verified={Boolean(verificationToken)}
            onVerified={(token) => setVerificationToken(token)}
            onReset={() => setVerificationToken(null)}
            error={errors.phone}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            {...field({
              label: form.emailReceipt ? '이메일' : '이메일 (선택)',
              name: 'email',
              type: 'email',
              value: form.email,
              error: Boolean(errors.email),
              // 이메일로 견적받기를 선택했을 때만 필수가 된다
              helperText: errors.email || (form.emailReceipt ? '견적서를 보낼 주소' : ''),
              required: form.emailReceipt,
            })}
          />
        </Grid>


        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name='emailReceipt'
                checked={form.emailReceipt}
                onChange={handleChange}
                sx={{ color: '#5A4231', '&.Mui-checked': { color: '#5A4231' } }}
              />
            }
            label='이메일로 견적받기'
          />
        </Grid>

        {!isCommute && (
          <Grid item xs={12}>
            <SelectField
              label='여행구분'
              name='travelType'
              value={form.travelType}
              onChange={handleChange}
              options={TRAVEL_TYPES}
            />
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <SelectField
            label='차량구분'
            name='vehicleType'
            value={form.vehicleType}
            onChange={handleChange}
            options={VEHICLE_TYPES}
            error={errors.vehicleType}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectField
            label='차량대수'
            name='vehicleNumber'
            value={form.vehicleNumber}
            onChange={handleChange}
            options={VEHICLE_NUMBERS}
            error={errors.vehicleNumber}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            {...field({
              label: '탑승인원',
              name: 'boardingCount',
              value: form.boardingCount,
              onKeyDown: blockNonDigitKey,
              inputProps: { maxLength: 4, inputMode: 'numeric' },
              helperText: '차량 1대당 탑승 인원',
            })}
          />
        </Grid>

        {isCommute ? (
          <>
            <Grid item xs={12} md={6}>
              <SelectField
                label='출근(출발) 시간'
                name='commuteStartTime'
                value={form.commuteStartTime}
                onChange={handleChange}
                options={COMMUTE_TIME_OPTIONS}
                error={errors.commuteStartTime}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectField
                label='퇴근 시간'
                name='commuteEndTime'
                value={form.commuteEndTime}
                onChange={handleChange}
                options={COMMUTE_TIME_OPTIONS}
                error={errors.commuteEndTime}
                required
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                {...field({
                  label: '출발일자',
                  name: 'departDate',
                  type: 'date',
                  value: form.departDate,
                  InputLabelProps: { shrink: true },
                  inputProps: { min: minDate },
                  error: Boolean(errors.departDate),
                  helperText: errors.departDate,
                })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectField
                label='출발시간'
                name='departTime'
                value={form.departTime}
                onChange={handleChange}
                options={TIME_OPTIONS}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                {...field({
                  label: '귀행일자',
                  name: 'arrivalDate',
                  type: 'date',
                  value: form.arrivalDate,
                  InputLabelProps: { shrink: true },
                  inputProps: { min: minDate },
                  error: Boolean(errors.arrivalDate),
                  helperText: errors.arrivalDate,
                })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectField
                label='귀행시간'
                name='arrivalTime'
                value={form.arrivalTime}
                onChange={handleChange}
                options={TIME_OPTIONS}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} md={6}>
          <PlaceAutocomplete
            label='출발지'
            name='departPlace'
            value={form.departPlace}
            onChange={(next) => {
              setForm((prev) => ({ ...prev, departPlace: next }));
              if (errors.departPlace) setErrors((prev) => ({ ...prev, departPlace: '' }));
            }}
            error={errors.departPlace}
            helperText={errors.departPlace}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <PlaceAutocomplete
            label='도착지'
            name='arrivalPlace'
            value={form.arrivalPlace}
            onChange={(next) => {
              setForm((prev) => ({ ...prev, arrivalPlace: next }));
              if (errors.arrivalPlace) setErrors((prev) => ({ ...prev, arrivalPlace: '' }));
            }}
            error={errors.arrivalPlace}
            helperText={errors.arrivalPlace}
            required
          />
        </Grid>

        {isCommute && (
          <Grid item xs={12} md={4}>
            <SelectField
              label='경유지 수'
              name='stopCount'
              value={form.stopCount}
              onChange={handleChange}
              options={STOP_COUNTS.map((count) => ({ value: count, label: `${count}곳` }))}
            />
          </Grid>
        )}
        <Grid item xs={12} md={isCommute ? 8 : 12}>
          <TextField
            {...field({
              label: '경유지',
              name: 'stopPlace',
              value: form.stopPlace,
              autoComplete: 'off',
              helperText: isCommute ? '경유지가 여러 곳이면 순서대로 입력해주세요.' : '',
            })}
          />
        </Grid>

        {isCommute && (
          <>
            <Grid item xs={12} md={6}>
              <SelectField
                label='운행형태'
                name='workType'
                value={form.workType}
                onChange={handleChange}
                options={WORK_TYPES}
                error={errors.workType}
                helperText='예) 출근/퇴근, 1일 3교대'
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectField
                label='근무일'
                name='workDays'
                value={form.workDays}
                onChange={handleChange}
                options={WORK_DAYS}
                error={errors.workDays}
                helperText='예) 주5일, 주6일, 매일'
                required
              />
            </Grid>
          </>
        )}

        {!isCommute && (
          <Grid item xs={12} md={4}>
            <RadioField
              label='왕복 구분'
              name='wayType'
              value={form.wayType}
              onChange={handleChange}
              options={WAY_TYPES}
            />
          </Grid>
        )}
        <Grid item xs={12} md={isCommute ? 6 : 4}>
          <RadioField
            label='결제 방법'
            name='payment'
            value={form.payment}
            onChange={handleChange}
            options={PAYMENT_METHODS}
          />
        </Grid>
        <Grid item xs={12} md={isCommute ? 6 : 4}>
          <RadioField
            label='세금 계산서'
            name='taxBill'
            value={form.taxBill}
            onChange={handleChange}
            options={TAX_BILL_OPTIONS}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            {...field({
              label: '기타 메모 사항',
              name: 'memo',
              value: form.memo,
              multiline: true,
              minRows: 2,
              autoComplete: 'off',
              // maxLength 로 막지 않는다 — 붙여넣기한 긴 글이 소리 없이 잘려 손님이 모르고 보낸다.
              // 대신 글자 수를 보여 주고, 넘으면 빨갛게 표시해 접수 전에 줄이게 한다.
              error: Boolean(errors.memo) || form.memo.trim().length > MEMO_MAX_LENGTH,
              helperText: errors.memo ||
                `${form.memo.trim().length.toLocaleString()} / ${MEMO_MAX_LENGTH.toLocaleString()}자`,
              FormHelperTextProps: { sx: { textAlign: 'right' } },
            })}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant='caption' sx={{ color: '#6b5544', display: 'block', mb: 1 }}>
            * 표시는 필수 입력 항목입니다. 견적 조회·수정은 휴대폰 본인확인(문자 인증)으로 진행됩니다.
          </Typography>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            size='large'
            sx={{
              backgroundColor: accentColor,
              color: '#FCFCFC',
              borderRadius: '10px',
              fontSize: 17,
              fontWeight: 800,
              py: 1.4,
              boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
              '&:hover': { backgroundColor: accentColor, filter: 'brightness(0.92)' },
            }}
          >
            {isModify ? '수정 내용 저장' : isCommute ? '통근·셔틀버스 견적요청' : '견적요청'}
          </Button>
        </Grid>
      </Grid>

      <Loading open={loading} />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={closeToast} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
